import {
  FamilyHistory,
  MedicalHistory,
  Patientallergy,
  PatientMedication,
  SurgicalHistory,
} from "../services/api";
import { formatDate } from "./date-time-utility";
import { getValueFromObj } from "./utils";
import { SEVERITY_RECORD } from "../pages/app/provider/patient/patient-list/patient-chart-new/allergy/allergy-constant";
import { RELATIVE_MAPPER } from "../pages/app/provider/patient/patient-list/patient-chart-new/history/history-constant";
import {
  DOSAGE_LIST_MAPPER,
  FREQUENCY_LIST_MAPPER,
} from "../pages/app/provider/patient/patient-list/patient-chart-new/medication/medication-constant";

export type Cell = string | number | null | undefined;

/**
 * A Subjective section: how to name a row, and which detail fields it carries.
 *
 * Both surfaces read the same section and render it the same way — one line per
 * row, "Name | Label: Value | ..." — so they can no longer list different
 * fields. The report was previously missing Onset Date / Notes / Relation /
 * Onset Age because each surface built its own list.
 */
export type EntrySection<T> = {
  title: (row: T) => Cell;
  columns: { label: string; value: (row: T) => Cell }[];
};

/** Trimmed display text; null/undefined and whitespace collapse to "". */
const text = (value: Cell) => (value == null ? "" : String(value).trim());

/** Blank for missing or unparseable dates, so "Invalid Date" never prints. */
const date = (value?: string) => {
  const formatted = value ? formatDate(value) : "";
  return !formatted || formatted.includes("Invalid") ? "" : formatted;
};

/** Coded value to its display label, falling back to the raw code. */
const mapLabel = (dict: Record<string, string>, value?: string) =>
  value ? (dict[value] ?? value) : "";

export const ALLERGY_SECTION: EntrySection<Patientallergy> = {
  title: (row) => row.allergy,
  columns: [
    {
      label: "Severity",
      value: (row) => mapLabel(SEVERITY_RECORD, row.severity),
    },
    { label: "Reaction", value: (row) => row.reaction },
    { label: "Onset Date", value: (row) => date(row.onSetDate) },
  ],
};

export const MEDICAL_HISTORY_SECTION: EntrySection<MedicalHistory> = {
  title: (row) => row.conditionName,
  columns: [
    { label: "Diagnosed Date", value: (row) => date(row.medicalHistoryDate) },
    { label: "Note", value: (row) => row.note },
  ],
};

export const SURGICAL_HISTORY_SECTION: EntrySection<SurgicalHistory> = {
  title: (row) => row.surgeryName,
  columns: [
    { label: "Surgery Date", value: (row) => date(row.surgeryDate) },
    { label: "Notes", value: (row) => row.note },
  ],
};

export const FAMILY_HISTORY_SECTION: EntrySection<FamilyHistory> = {
  title: (row) => row.conditionName,
  columns: [
    // No raw fallback for Relation — an unmapped code is not meaningful to a
    // reader, matching how the visit note has always shown it.
    {
      label: "Relation",
      value: (row) => (row.relative ? RELATIVE_MAPPER[row.relative] : ""),
    },
    { label: "Onset Age", value: (row) => row.onSetAge },
    { label: "Note", value: (row) => row.note },
  ],
};

export const MEDICATION_SECTION: EntrySection<PatientMedication> = {
  title: (row) => (row.medicalCodes ? getValueFromObj(row.medicalCodes) : ""),
  columns: [
    {
      label: "Dosage",
      value: (row) =>
        `${row.quantity ?? ""} ${mapLabel(DOSAGE_LIST_MAPPER, row.dosage)}`.trim(),
    },
    {
      label: "Frequency",
      value: (row) => mapLabel(FREQUENCY_LIST_MAPPER, row.frequency),
    },
    { label: "Start Date", value: (row) => date(row.medicationStartDate) },
  ],
};

/** Populated columns only, as "Label: Value" segments. */
const filledFields = <T>(section: EntrySection<T>, row: T) =>
  section.columns
    .map(({ label, value }) => ({ label, value: text(value(row)) }))
    .filter((field) => field.value);

/**
 * The visit note's one-line form: "Name | Label: Value | ...".
 * Empty columns are skipped; an entry with nothing to show prints "-".
 */
export const buildEntryLine = <T>(section: EntrySection<T>, row: T) =>
  [
    text(section.title(row)),
    ...filledFields(section, row).map(
      ({ label, value }) => `${label}: ${value}`
    ),
  ]
    .filter(Boolean)
    .join(" | ") || "-";

// Status was dropped from every Subjective entry per BA: it was shown only on
// Allergies and Family History, so PMH/PSH read as inconsistent beside them.
// Kept for discoverability in case it is reinstated.
// const statusLabel = (active?: boolean | null) =>
//   active == null ? "" : active ? "Active" : "Inactive";
